
import React from "react";

// ========================================
// PRODUCT BENEFITS & DIFFERENTIATORS
// ========================================

const ProductBenefits = ({
    benefits = [],
    differentiators = [],
    performanceClaims = [],
    onChange
}) => {

    // ========================================
    // DATA
    // ========================================

    const getItems = (field) => {

        const data = {
            benefits,
            differentiators,
            performanceClaims
        };

        return Array.isArray(data[field])
            ? data[field]
            : [];

    };


    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";


    // ========================================
    // ITEM MANAGEMENT
    // ========================================

    const addItem = (field) => {

        onChange(
            field,
            [
                ...getItems(field),
                {
                    text: ""
                }
            ]
        );

    };


    const updateItem = (
        field,
        index,
        value
    ) => {

        const updatedItems = getItems(field).map(
            (item, i) =>
                i === index
                    ? {
                        ...item,
                        text: value
                    }
                    : item
        );

        onChange(field, updatedItems);

    };


    const removeItem = (
        field,
        index
    ) => {

        onChange(
            field,
            getItems(field).filter(
                (_, i) => i !== index
            )
        );

    };


    // ========================================
    // REUSABLE SECTION
    // ========================================

    const renderSection = (
        field,
        title,
        description,
        placeholder,
        addLabel
    ) => {

        const items = getItems(field);

        return (

            <section className="space-y-5">

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                    <div>

                        <h3 className="text-base font-semibold text-gray-900">
                            {title}
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                            {description}
                        </p>

                    </div>

                    <button
                        type="button"
                        onClick={() => addItem(field)}
                        className={addButtonClass}
                    >
                        + {addLabel}
                    </button>

                </div>


                {items.length === 0 && (

                    <div className="py-8 text-center border border-dashed rounded-lg">

                        <p className="text-sm text-gray-500">
                            No entries added yet.
                        </p>

                    </div>

                )}


                {items.map((item, index) => (

                    <div
                        key={item._id || index}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    >

                        <div className="flex flex-col sm:flex-row gap-3">

                            <div className="flex-1 min-w-0">

                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {title} — {index + 1}
                                </label>

                                <textarea
                                    value={item.text || ""}
                                    onChange={(event) =>
                                        updateItem(
                                            field,
                                            index,
                                            event.target.value
                                        )
                                    }
                                    placeholder={placeholder}
                                    rows={3}
                                    className={inputClass}
                                />

                            </div>

                            <div className="sm:self-end">

                                <button
                                    type="button"
                                    onClick={() =>
                                        removeItem(field, index)
                                    }
                                    className={removeButtonClass}
                                >
                                    Remove
                                </button>

                            </div>

                        </div>

                    </div>

                ))}

            </section>

        );

    };


    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-10">

            {renderSection(
                "benefits",
                "Product Benefits",
                "Describe the advantages customers gain from using this product.",
                "e.g. Helps improve water resistance in approved applications.",
                "Benefit"
            )}


            {renderSection(
                "differentiators",
                "Product Differentiators",
                "Describe what distinguishes this product from other available solutions.",
                "e.g. Specific formulation or application advantage supported by product documentation.",
                "Differentiator"
            )}


            {renderSection(
                "performanceClaims",
                "Performance Claims",
                "Record only technical or performance claims supported by verified product information.",
                "e.g. Tested performance result with applicable standard and conditions.",
                "Performance Claim"
            )}

            <p className="text-xs text-gray-500 border-t pt-4">
                Performance claims should be verified against
                approved technical documentation before the
                product is made publicly available to the AI agent.
            </p>

        </div>

    );

};

export default ProductBenefits;
